import React, { useEffect, useState, useMemo } from 'react';

/**
 * ScrollProgressBar
 * Thin sticky bar at the very top of the viewport (above the header).
 * - Fills left→right based on document scroll progress (gradient sweep).
 * - Renders discrete tick markers for each landmark section id, lighting
 *   up when the user has scrolled past that section.
 */
const SECTIONS = [
  { id: 'challenges',     label: 'Herausforderungen' },
  { id: 'lead-loss',      label: 'Problem' },
  { id: 'why-nothing',    label: 'Warum' },
  { id: 'masterclass',    label: 'Lösung' },
  { id: 'results',        label: 'Erfolge' },
  { id: 'features',       label: 'System' },
  { id: 'transformation', label: 'Vorher/Nachher' },
  { id: 'faq',            label: 'FAQ' },
  { id: 'cta',            label: 'Kontakt' },
];

const ScrollProgressBar = () => {
  const [progress, setProgress] = useState(0); // 0..1 doc scroll progress
  const [activeId, setActiveId] = useState(null);
  const [ticks, setTicks] = useState([]); // [{id,label,ratio}]

  // Compute tick positions (recompute on resize + on route change)
  useEffect(() => {
    const compute = () => {
      const docH = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const next = SECTIONS.map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return null;
        const top = el.getBoundingClientRect().top + window.scrollY;
        return { ...s, ratio: Math.max(0, Math.min(1, top / docH)) };
      }).filter(Boolean);
      setTicks(next);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(document.body);
    window.addEventListener('load', compute);
    // Recompute after lazy sections settle
    const t1 = setTimeout(compute, 1500);
    const t2 = setTimeout(compute, 3500);
    return () => {
      ro.disconnect();
      window.removeEventListener('load', compute);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Scroll listener
  useEffect(() => {
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const docH = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        setProgress(Math.min(1, window.scrollY / docH));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section = last one whose top is above viewport center
  useEffect(() => {
    const midlineTargets = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (midlineTargets.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        // Track the entry whose center is nearest the top-third of the viewport
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-25% 0px -60% 0px', threshold: [0, 0.1] }
    );
    midlineTargets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fillStyle = useMemo(() => ({ width: `${(progress * 100).toFixed(2)}%` }), [progress]);

  return (
    <div className="scroll-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
      <div className="scroll-progress-track">
        <div className="scroll-progress-fill" style={fillStyle} />
        {ticks.map((t) => {
          const isActive = activeId === t.id;
          const isPassed = progress >= t.ratio - 0.005;
          return (
            <button
              key={t.id}
              type="button"
              className={`scroll-progress-tick ${isPassed ? 'is-passed' : ''} ${isActive ? 'is-active' : ''}`}
              style={{ left: `${t.ratio * 100}%` }}
              onClick={() => jumpTo(t.id)}
              aria-label={`Zu ${t.label} springen`}
              data-testid={`progress-tick-${t.id}`}
              title={t.label}
            >
              <span className="scroll-progress-tick-dot" />
              <span className="scroll-progress-tick-label">{t.label}</span>
            </button>
          );
        })}
      </div>

      <style>{`
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          z-index: 101; /* above header */
          pointer-events: none;
        }
        .scroll-progress-track {
          position: relative;
          height: 100%;
          background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02));
        }
        .scroll-progress-fill {
          position: absolute;
          top: 0; left: 0;
          height: 100%;
          background: linear-gradient(90deg, #00FFD1 0%, #00D4FF 45%, #A855F7 100%);
          box-shadow: 0 0 12px rgba(0, 255, 209, 0.55), 0 0 3px rgba(0, 212, 255, 0.6);
          transition: width 0.12s linear;
          border-radius: 0 2px 2px 0;
        }
        .scroll-progress-tick {
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          pointer-events: auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        .scroll-progress-tick-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.28);
          transition: all 0.35s ease;
        }
        .scroll-progress-tick.is-passed .scroll-progress-tick-dot {
          background: #00FFD1;
          border-color: #00FFD1;
          box-shadow: 0 0 10px #00FFD1;
        }
        .scroll-progress-tick.is-active .scroll-progress-tick-dot {
          background: #FFFFFF;
          border-color: #00FFD1;
          box-shadow: 0 0 16px #00FFD1, 0 0 0 3px rgba(0, 255, 209, 0.18);
          transform: scale(1.35);
        }
        .scroll-progress-tick-label {
          position: absolute;
          top: 14px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.85);
          background: rgba(10, 12, 22, 0.85);
          border: 1px solid rgba(0, 255, 209, 0.3);
          padding: 4px 10px;
          border-radius: 999px;
          white-space: nowrap;
          opacity: 0;
          transform: translateY(-4px);
          transition: opacity 0.25s ease, transform 0.25s ease;
          pointer-events: none;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        .scroll-progress-tick:hover .scroll-progress-tick-label,
        .scroll-progress-tick:focus-visible .scroll-progress-tick-label {
          opacity: 1;
          transform: translateY(0);
        }
        .scroll-progress-tick:focus-visible .scroll-progress-tick-dot {
          outline: 2px solid rgba(0,255,209,0.7);
          outline-offset: 3px;
        }

        @media (max-width: 640px) {
          .scroll-progress-tick-dot { width: 5px; height: 5px; }
          /* Hide labels on very small screens (they'd overlap) */
          .scroll-progress-tick-label { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .scroll-progress-fill,
          .scroll-progress-tick-dot,
          .scroll-progress-tick-label { transition: none; }
        }
      `}</style>
    </div>
  );
};

export default ScrollProgressBar;
